import React from 'react';
import MDEditor, { commands } from '@uiw/react-md-editor/nohighlight';

type MarkdownEditorFieldProps = {
  label: React.ReactNode;
  required?: boolean;
  value: string;
  onChange: (nextValue: string) => void;
  placeholder?: string;
  error?: React.ReactNode;
  testId?: string;
  errorTestId?: string;
  wrapperClassName?: string;
  height?: number;
};

const clearCommand = {
  name: 'clear',
  keyCommand: 'clear',
  buttonProps: {
    'aria-label': 'Clear description',
    title: 'Clear',
  },
  icon: (
    <svg width="12" height="12" viewBox="0 0 448 512" role="img" aria-hidden="true">
      <path
        fill="currentColor"
        d="M135.2 17.7C140.7 7.4 151.5 0 163.2 0H284.8c11.7 0 22.5 7.4 28 17.7L328 32H432c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16h-16l-21.2 339.1c-1.5 24.1-21.5 42.9-45.6 42.9H98.8c-24.1 0-44.1-18.8-45.6-42.9L32 96H16C7.2 96 0 88.8 0 80V48c0-8.8 7.2-16 16-16H120l15.2-14.3zM171.8 64l-7.1 14.3h118.6L276.2 64H171.8z"
      />
    </svg>
  ),
  execute: (state: any, api: any) => {
    api.setSelectionRange({ start: 0, end: (state?.text || '').length });
    api.replaceSelection('');
  },
};

const undoCommand = {
  name: 'undo',
  keyCommand: 'undo',
  buttonProps: {
    'aria-label': 'Undo',
    title: 'Undo',
  },
  icon: (
    <svg width="12" height="12" viewBox="0 0 512 512" role="img" aria-hidden="true">
      <path
        fill="currentColor"
        d="M255.1 8c-66.3 0-126.3 26.9-169.7 70.3L64 99.7V48c0-8.8-7.2-16-16-16H16C7.2 32 0 39.2 0 48v176c0 8.8 7.2 16 16 16h176c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16H116.3l32.6-32.6C176.1 116.2 213.6 96 255.1 96c80.5 0 145.9 65.4 145.9 145.9S335.6 387.8 255.1 387.8H128c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h127.1C370 451.8 464 357.8 464 241.9S370 8 255.1 8z"
      />
    </svg>
  ),
  execute: () => {
    try { document.execCommand('undo'); } catch { /* ignore */ }
  },
};

const redoCommand = {
  name: 'redo',
  keyCommand: 'redo',
  buttonProps: {
    'aria-label': 'Redo',
    title: 'Redo',
  },
  icon: (
    <svg width="12" height="12" viewBox="0 0 512 512" role="img" aria-hidden="true">
      <path
        fill="currentColor"
        d="M256.9 8c66.3 0 126.3 26.9 169.7 70.3L448 99.7V48c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16v176c0 8.8-7.2 16-16 16H320c-8.8 0-16-7.2-16-16v-32c0-8.8 7.2-16 16-16h75.7l-32.6-32.6C335.9 116.2 298.4 96 256.9 96c-80.5 0-145.9 65.4-145.9 145.9S176.4 387.8 256.9 387.8H384c8.8 0 16 7.2 16 16v32c0 8.8-7.2 16-16 16H256.9C142 451.8 48 357.8 48 241.9S142 8 256.9 8z"
      />
    </svg>
  ),
  execute: () => {
    try { document.execCommand('redo'); } catch { /* ignore */ }
  },
};

const underlineCommand = {
  name: 'underline',
  keyCommand: 'underline',
  buttonProps: {
    'aria-label': 'Underline',
    title: 'Underline',
  },
  icon: (
    <svg width="12" height="12" viewBox="0 0 24 24" role="img" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6 3v7a6 6 0 0 0 12 0V3h-2v7a4 4 0 0 1-8 0V3H6zm-1 18v2h14v-2H5z"
      />
    </svg>
  ),
  execute: (state: any, api: any) => {
    const open = '<u>';
    const close = '</u>';
    const start = state?.selection?.start ?? 0;
    const selectedText = state?.selectedText ?? '';

    if (!selectedText) {
      api.replaceSelection(`${open}${close}`);
      api.setSelectionRange({ start: start + open.length, end: start + open.length });
      return;
    }

    api.replaceSelection(`${open}${selectedText}${close}`);
    api.setSelectionRange({
      start: start + open.length,
      end: start + open.length + selectedText.length,
    });
  },
};

const headingGroup = (commands as any).group(
  [
    (commands as any).heading1,
    (commands as any).heading2,
    (commands as any).heading3,
    (commands as any).heading4,
  ],
  {
    name: 'heading',
    groupName: 'heading',
    buttonProps: {
      'aria-label': 'Heading level',
      title: 'Heading',
    },
    // Reuse the built-in heading icon so the toolbar matches the expected “TITL”/heading button.
    icon: (commands as any).heading?.icon,
  }
);

function RequiredDot({ required }: { required?: boolean }) {
  if (!required) return null;
  return <span className="required-dot" aria-label="required">*</span>;
}

export default function MarkdownEditorField({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  error,
  testId,
  errorTestId,
  wrapperClassName = 'field',
  height = 180,
}: MarkdownEditorFieldProps) {
  const finalErrorTestId = errorTestId || (testId ? `${testId}-error` : undefined);

  return (
    <div className={wrapperClassName}>
      <label className="label">{label} <RequiredDot required={required} /></label>

      <div
        className={`rid-md-editor ${error ? 'error' : ''}`}
        data-color-mode="light"
        data-testid={testId}
      >
        <MDEditor
          value={value}
          onChange={(next) => onChange(next ?? '')}
          preview="edit"
          height={height}
          // Hide the right-side mode toggles edit, live, preview, fullscreen.
          extraCommands={[]}
          commands={[
            headingGroup,
            commands.bold,
            commands.italic,
            underlineCommand as any,
            commands.strikethrough,
            commands.divider,
            commands.unorderedListCommand,
            commands.orderedListCommand,
            commands.quote,
            commands.code,
            // Second row table, image, link, clear, undo, redo.
            commands.divider,
            commands.table,
            commands.link,
            clearCommand as any,
            undoCommand as any,
            redoCommand as any,
          ]}
          textareaProps={{
            placeholder,
          }}
        />
      </div>

      {error && <div className="error-text" data-testid={finalErrorTestId}>{error}</div>}
    </div>
  );
}
